/**
 * PATH: apps/quote/src/pages/import-quote/existingQuoteFamilies.ts
 * FILE_ID: QUOTE-IMPORT-EXISTING-FAMILIES-001
 * PURPOSE: Group list/job/version rows into quote families and selection rules.
 * WHY: Ticking a family defaults every revision/variation on; individuals stay untickable.
 */
import type { JobQuoteEntry, QuoteListItem, QuoteVersionRow } from '@/lib/quote-api-types'
import type { ImportableQuote, QuoteFamily } from './existingQuoteTypes'

export function revisionLabel(version: number | null | undefined): string {
  if (version == null || !Number.isFinite(version)) return '—'
  return version <= 1 ? 'Original' : `R${version - 1}`
}

export function quoteFamilyKey(quoteNumber: string): string {
  const trimmed = (quoteNumber || '').trim()
  const stripped = trimmed.replace(/-?R\d+$/i, '')
  return stripped || trimmed || 'untitled'
}

function toNum(value: string | number | null | undefined): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string' && value.trim()) {
    const n = Number.parseFloat(value)
    return Number.isFinite(n) ? n : null
  }
  return null
}

export function fromListItem(q: QuoteListItem): ImportableQuote {
  return {
    key: `b:${q.id}`,
    quoteNumber: q.quote_number,
    name: q.display_name || q.project_name || q.quote_number,
    revision: q.revision ?? null,
    revisionLabel: q.revision_label || revisionLabel(q.revision),
    status: q.status_display || String(q.status || ''),
    totalExcl: toNum(q.total_excl_gst),
    clientName: q.client_name,
    projectName: q.project_name,
    builderQuoteId: q.id,
    sheetId: null,
    jobId: q.job_number ?? null,
    isVariation: (q.quote_type || '').toLowerCase().includes('variation'),
  }
}

export function fromJobEntry(e: JobQuoteEntry, jobId: number | string): ImportableQuote {
  return {
    key: e.builder_quote_id ? `b:${e.builder_quote_id}` : `s:${jobId}:${e.sheet_id}`,
    quoteNumber: e.quote_number,
    name: e.quote_name || e.quote_number,
    revision: e.revision,
    revisionLabel: revisionLabel(e.revision),
    status: e.status,
    totalExcl: Number.isFinite(e.total_excl_gst) ? e.total_excl_gst : null,
    builderQuoteId: e.builder_quote_id,
    sheetId: e.sheet_id,
    jobId,
    isVariation: e.is_variation,
    isContract: e.is_contract,
  }
}

export function fromVersionRow(
  v: QuoteVersionRow,
  jobId?: number | string | null,
): ImportableQuote | null {
  if (!v.builder_quote_id) return null
  return {
    key: `b:${v.builder_quote_id}`,
    quoteNumber: v.quote_number,
    name: v.title || v.quote_number,
    revision: v.version ?? null,
    revisionLabel: v.version_label || v.label || revisionLabel(v.version),
    status: v.status || '',
    totalExcl: toNum(v.value),
    builderQuoteId: v.builder_quote_id,
    sheetId: null,
    jobId: jobId ?? null,
  }
}

export function dedupeByKey(items: ImportableQuote[]): ImportableQuote[] {
  const seen = new Map<string, ImportableQuote>()
  for (const item of items) {
    if (!seen.has(item.key)) seen.set(item.key, item)
  }
  return [...seen.values()]
}

export function groupFamilies(
  items: ImportableQuote[],
  extrasByFamily: Record<string, ImportableQuote[]> = {},
): QuoteFamily[] {
  const buckets = new Map<string, ImportableQuote[]>()
  for (const item of items) {
    const key = quoteFamilyKey(item.quoteNumber)
    const list = buckets.get(key) || []
    list.push(item)
    buckets.set(key, list)
  }
  for (const [key, extras] of Object.entries(extrasByFamily)) {
    const list = buckets.get(key) || []
    list.push(...extras)
    buckets.set(key, list)
  }
  return [...buckets.entries()]
    .map(([familyKey, members]) => {
      const unique = dedupeByKey(members).sort((a, b) =>
        (a.revision ?? 0) - (b.revision ?? 0) || a.quoteNumber.localeCompare(b.quoteNumber),
      )
      return {
        familyKey,
        label: unique[0]?.quoteNumber || familyKey,
        members: unique,
      }
    })
    .sort((a, b) => a.label.localeCompare(b.label))
}

export function toggleMember(selected: Set<string>, key: string): Set<string> {
  const next = new Set(selected)
  if (next.has(key)) next.delete(key)
  else next.add(key)
  return next
}

export function toggleFamily(selected: Set<string>, family: QuoteFamily): Set<string> {
  const keys = family.members.map((m) => m.key)
  const allOn = keys.length > 0 && keys.every((k) => selected.has(k))
  const next = new Set(selected)
  if (allOn) keys.forEach((k) => next.delete(k))
  else keys.forEach((k) => next.add(k))
  return next
}

/** First tick in a family selects every member; later ticks toggle one row. */
export function selectOnMemberTick(
  selected: Set<string>,
  family: QuoteFamily,
  clickedKey: string,
): Set<string> {
  const keys = family.members.map((m) => m.key)
  const anySelected = keys.some((k) => selected.has(k))
  if (!anySelected) {
    const next = new Set(selected)
    keys.forEach((k) => next.add(k))
    return next
  }
  return toggleMember(selected, clickedKey)
}

export function selectNewFamilyMembers(
  selected: Set<string>,
  previousKeys: string[],
  family: QuoteFamily,
): Set<string> {
  const wasAll = previousKeys.length > 0 && previousKeys.every((k) => selected.has(k))
  if (!wasAll) return selected
  const next = new Set(selected)
  family.members.forEach((m) => next.add(m.key))
  return next
}

export function matchesSearch(item: ImportableQuote, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return [item.quoteNumber, item.name, item.clientName, item.projectName, item.revisionLabel]
    .some((part) => (part || '').toLowerCase().includes(q))
}
